/**
 * AgentManagement Component
 * 
 * Admin UI for managing Azure AI Foundry Agents with Bing grounding.
 * Supports creating agents with:
 * - General Bing Web Search (searches entire web)
 * - Bing Custom Search (domain-restricted search)
 * - No grounding (basic agent)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Stack,
  Text,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  DefaultButton,
  Dialog,
  DialogType,
  DialogFooter,
  TextField,
  Dropdown,
  IDropdownOption,
  Toggle,
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  SelectionMode,
  IconButton,
  Panel,
  PanelType,
  Label,
  SpinButton,
  Separator,
  TooltipHost,
  DirectionalHint,
  mergeStyleSets,
  FontWeights,
  Pivot,
  PivotItem,
} from '@fluentui/react';
import {
  BotRegular,
  AddRegular,
  DeleteRegular,
  EditRegular,
  SearchRegular,
  GlobeRegular,
  InfoRegular,
  CheckmarkCircleRegular,
  WarningRegular,
  ArrowSyncRegular,
  SettingsRegular,
} from '@fluentui/react-icons';
import { authenticatedFetch } from '../../utils/authApi';
import CustomSearchConfigManagement from './CustomSearchConfigManagement';

// Types
interface ToolDetail {
  type: string;
  config: {
    connection_id?: string;
    sites?: Array<{ url: string; boost_level: string }>;
    count?: number;
    freshness?: string;
    market?: string;
    vector_store_ids?: string[];
    index_name?: string;
    index_connection_id?: string;
    max_num_results?: number;
  };
}

interface Agent {
  id: string;
  name: string;
  description?: string;
  model?: string;
  tools_configured?: boolean;
  temperature?: number;
  instructions?: string;
  top_p?: number;
  grounding_type?: string;
  custom_search_config_name?: string;
  tool_details?: ToolDetail[];
  tools?: string[];
}

interface GroundingTypeInfo {
  type: string;
  name: string;
  description: string;
  requires?: string;
  config_fields: Array<{
    name: string;
    type: string;
    required: boolean;
    default?: any;
    description: string;
  }>;
}

interface ServiceStatus {
  available: boolean;
  message: string;
}

interface CustomSearchConfig {
  id: string;
  name: string;
  allowedDomains: Array<{ domain: string; includeSubPages: boolean; boostLevel?: string }>;
  blockedDomains: Array<{ domain: string; includeSubPages: boolean; boostLevel?: string }>;
}

// Grounding type options
const GROUNDING_TYPES: IDropdownOption[] = [
  { key: 'none', text: 'No Grounding - Basic agent without web search' },
  { key: 'bing_search', text: 'Bing Web Search - Search the entire web' },
  { key: 'bing_custom_search', text: 'Bing Custom Search - Domain-restricted search' },
];

const FRESHNESS_OPTIONS: IDropdownOption[] = [
  { key: '', text: 'No restriction' },
  { key: 'Day', text: 'Last 24 hours' },
  { key: 'Week', text: 'Last 7 days' },
  { key: 'Month', text: 'Last 30 days' },
];

const MARKET_OPTIONS: IDropdownOption[] = [
  { key: 'he-IL', text: 'Hebrew - Israel (he-IL)' },
  { key: 'en-US', text: 'English - United States (en-US)' },
  { key: 'en-GB', text: 'English - United Kingdom (en-GB)' },
  { key: 'de-DE', text: 'German - Germany (de-DE)' },
  { key: 'fr-FR', text: 'French - France (fr-FR)' },
];

// Styles
const styles = mergeStyleSets({
  container: {
    padding: '20px',
    backgroundColor: '#fafafa',
    borderRadius: '8px',
    border: '1px solid #e1e1e1',
    minHeight: '500px',
  },
  header: {
    marginBottom: '20px',
  },
  statusCard: {
    padding: '16px',
    backgroundColor: 'white',
    borderRadius: '8px',
    border: '1px solid #e1e1e1',
    marginBottom: '20px',
  },
  agentCard: {
    padding: '16px',
    margin: '8px 0',
    border: '1px solid #e1e1e1',
    borderRadius: '8px',
    backgroundColor: 'white',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    transition: 'all 0.2s ease',
    ':hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      transform: 'translateY(-1px)',
    },
  },
  formSection: {
    padding: '16px',
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    marginBottom: '16px',
  },
  fieldInfo: {
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: '4px',
    cursor: 'help',
  },
});

// Info tooltip component
interface FieldInfoProps {
  title: string;
  description: string;
}

const FieldInfo: React.FC<FieldInfoProps> = ({ title, description }) => (
  <TooltipHost
    content={
      <div style={{ padding: '8px', maxWidth: '300px' }}>
        <Text variant="mediumPlus" style={{ fontWeight: FontWeights.semibold, display: 'block', marginBottom: '4px' }}>
          {title}
        </Text>
        <Text variant="small" style={{ color: '#666' }}>
          {description}
        </Text>
      </div>
    }
    directionalHint={DirectionalHint.topCenter}
    styles={{ root: { display: 'inline-flex', alignItems: 'center', marginLeft: '4px', cursor: 'help' } }}
  >
    <InfoRegular style={{ fontSize: '14px', color: '#667eea' }} />
  </TooltipHost>
);

interface AgentManagementProps {
  embedded?: boolean;
}

const AgentManagement: React.FC<AgentManagementProps> = ({ embedded = false }) => {
  // State
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [serviceStatus, setServiceStatus] = useState<ServiceStatus | null>(null);
  const [groundingTypes, setGroundingTypes] = useState<GroundingTypeInfo[]>([]);
  const [syncing, setSyncing] = useState(false);
  
  // Default values from admin config
  const [adminDefaults, setAdminDefaults] = useState<{ model: string; instructions: string }>({
    model: '',
    instructions: '',
  });
  
  // Tab state
  const [activeTab, setActiveTab] = useState<string>('agents');
  
  // Custom Search Config state
  const [customSearchConfigs, setCustomSearchConfigs] = useState<CustomSearchConfig[]>([]);
  const [selectedCustomSearchConfig, setSelectedCustomSearchConfig] = useState<CustomSearchConfig | null>(null);
  
  // Panel state
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingAgent, setDeletingAgent] = useState<Agent | null>(null);
  const [saving, setSaving] = useState(false);
  const [loadingAgent, setLoadingAgent] = useState(false);
  
  // Form state for create
  const [formData, setFormData] = useState({
    name: '',
    instructions: '',
    description: '',
    model: '',
    temperature: 0,
    grounding_type: 'none',
    // Bing config
    bing_count: 5,
    bing_freshness: '',
    bing_market: 'he-IL',
    // Custom search config - can select from saved configs
    custom_search_config_id: '',
    custom_sites: [] as Array<{ url: string; boost_level: string }>,
    custom_count: 5,
    custom_freshness: '',
    custom_market: 'he-IL',
  });

  // Form state for edit (includes grounding settings)
  const [editFormData, setEditFormData] = useState({
    name: '',
    instructions: '',
    description: '',
    model: '',
    temperature: 0,
    // Grounding settings
    grounding_type: 'none',
    bing_count: 5,
    bing_freshness: '',
    bing_market: 'he-IL',
    bing_set_lang: '',
    // Azure Bing Custom Search
    custom_search_config_name: '',  // ARM configuration name
    custom_count: 5,
    custom_freshness: '',
    custom_market: 'he-IL',
    custom_set_lang: '',
  });
  
  // Track if grounding was modified
  const [groundingModified, setGroundingModified] = useState(false);

  // Load service status
  const loadServiceStatus = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/api/admin/agents/service-status');
      if (response.ok) {
        const data = await response.json();
        setServiceStatus({ available: data.available, message: data.message });
      }
    } catch (err: any) {
      setServiceStatus({ available: false, message: 'Failed to check service status' });
    }
  }, []);

  // Load agents
  const loadAgents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authenticatedFetch('/api/admin/agents');
      if (response.ok) {
        const data = await response.json();
        setAgents(data.agents || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to load agents');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load grounding types and admin defaults
  const loadGroundingTypes = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/api/admin/agents/grounding-types');
      if (response.ok) {
        const data = await response.json();
        setGroundingTypes(data.grounding_types || []);
        // Store admin config defaults
        if (data.defaults) {
          setAdminDefaults({
            model: data.defaults.model || '',
            instructions: data.defaults.instructions || '',
          });
        }
      }
    } catch (err) {
      console.error('Failed to load grounding types:', err);
    }
  }, []);

  // Load custom search configurations
  const loadCustomSearchConfigs = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/api/admin/agents/custom-search-configs');
      if (response.ok) {
        const data = await response.json();
        setCustomSearchConfigs(data.configs || []);
      }
    } catch (err) {
      console.error('Failed to load custom search configs:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadServiceStatus();
    loadAgents();
    loadGroundingTypes();
    loadCustomSearchConfigs();
  }, [loadServiceStatus, loadAgents, loadGroundingTypes, loadCustomSearchConfigs]);

  // Reset form - use admin defaults from config
  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      instructions: adminDefaults.instructions,  // Pre-fill with admin config instructions
      description: '',
      model: adminDefaults.model,  // Pre-fill with admin config model
      temperature: 0,
      grounding_type: 'none',
      bing_count: 5,
      bing_freshness: '',
      bing_market: 'he-IL',
      custom_search_config_id: '',
      custom_sites: [],
      custom_count: 5,
      custom_freshness: '',
      custom_market: 'he-IL',
    });
    setSelectedCustomSearchConfig(null);
  }, [adminDefaults]);

  // Sync form with admin defaults when they load (for new agent creation)
  useEffect(() => {
    // Only update if create panel is open and form has empty instructions/model
    if (showCreatePanel) {
      if (adminDefaults.instructions && !formData.instructions) {
        setFormData(prev => ({ ...prev, instructions: adminDefaults.instructions }));
      }
      if (adminDefaults.model && !formData.model) {
        setFormData(prev => ({ ...prev, model: adminDefaults.model }));
      }
    }
  }, [adminDefaults, showCreatePanel]);

  // Create agent
  const handleCreate = async () => {
    if (!formData.name || !formData.instructions) {
      setError('Agent name and instructions are required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const payload: any = {
        name: formData.name,
        instructions: formData.instructions,
        description: formData.description || undefined,
        model: formData.model || undefined,
        temperature: formData.temperature,
        grounding_type: formData.grounding_type,
      };

      // Add Bing config if using bing_search
      if (formData.grounding_type === 'bing_search') {
        payload.bing_config = {
          count: formData.bing_count,
          freshness: formData.bing_freshness || undefined,
          market: formData.bing_market,
        };
      }

      // Add Custom Search config if using bing_custom_search (Azure)
      if (formData.grounding_type === 'bing_custom_search') {
        // Use selected configuration name from ARM API
        const config = selectedCustomSearchConfig;
        if (config) {
          payload.custom_search_config = {
            configuration_name: config.name,  // ARM config name
            count: formData.custom_count,
            freshness: formData.custom_freshness || undefined,
            market: formData.custom_market,
          };
        } else {
          // No configuration selected - error will be returned by backend
          payload.custom_search_config = {
            configuration_name: '',  // Will cause validation error
            count: formData.custom_count,
            freshness: formData.custom_freshness || undefined,
            market: formData.custom_market,
          };
        }
      }

      const response = await authenticatedFetch('/api/admin/agents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(`Agent "${data.agent?.name || formData.name}" created successfully`);
        setShowCreatePanel(false);
        resetForm();
        loadAgents();
        setTimeout(() => setSuccess(null), 5000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to create agent');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create agent');
    } finally {
      setSaving(false);
    }
  };

  // Delete agent
  const handleDelete = async () => {
    if (!deletingAgent) return;

    try {
      setSaving(true);
      setError(null);

      const response = await authenticatedFetch(`/api/admin/agents/${deletingAgent.id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setSuccess(`Agent "${deletingAgent.name}" deleted successfully`);
        setShowDeleteDialog(false);
        setDeletingAgent(null);
        loadAgents();
        setTimeout(() => setSuccess(null), 5000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to delete agent');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete agent');
    } finally {
      setSaving(false);
    }
  };

  // Sync Custom Search Instructions to all agents
  const handleSyncCustomSearchInstructions = async (dryRun: boolean = false) => {
    try {
      setSyncing(true);
      setError(null);
      setSuccess(null);

      const response = await authenticatedFetch('/api/admin/agents/sync-custom-search-instructions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dry_run: dryRun }),
      });

      const data = await response.json();

      if (data.success) {
        const action = dryRun ? 'would be updated' : 'updated';
        const message = `Sync ${dryRun ? 'preview' : 'complete'}: ${data.total_updated} agents ${action}, ${data.skipped_agents?.length || 0} skipped (no custom search)`;
        
        if (data.updated_agents?.length > 0) {
          setSuccess(`${message}. Updated: ${data.updated_agents.join(', ')}`);
        } else {
          setSuccess(message);
        }
        
        if (!dryRun) {
          loadAgents(); // Refresh agents list after sync
        }
        setTimeout(() => setSuccess(null), 8000);
      } else {
        setError(data.error || 'Failed to sync custom search instructions');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to sync custom search instructions');
    } finally {
      setSyncing(false);
    }
  };

  // Load agent details for editing
  const loadAgentDetails = async (agent: Agent) => {
    try {
      setLoadingAgent(true);
      setError(null);
      setGroundingModified(false);
      
      const response = await authenticatedFetch(`/api/admin/agents/${agent.id}`);
      if (response.ok) {
        const data = await response.json();
        const agentData = data.agent;
        
        setEditingAgent(agentData);
        
        // Get grounding config from dedicated grounding_config object (reliable) 
        // with fallback to tool_details for backwards compatibility
        const groundingConfig = agentData.grounding_config || {};
        const toolDetail = agentData.tool_details?.[0];
        const toolConfig = toolDetail?.config || {};
        
        // Log the actual config from Azure for debugging
        console.log('[AgentManagement] Agent grounding_config:', groundingConfig);
        console.log('[AgentManagement] Agent tool_details:', agentData.tool_details);
        
        // Use grounding_config values (from metadata) with fallback to toolConfig
        const count = groundingConfig.count ?? toolConfig.count ?? 5;
        const freshness = groundingConfig.freshness || toolConfig.freshness || '';
        const market = groundingConfig.market || toolConfig.market || 'he-IL';
        const setLang = groundingConfig.set_lang || toolConfig.set_lang || '';
        const customSearchConfigName = groundingConfig.custom_search_configuration_name || 
                                       toolConfig.custom_search_configuration_name || 
                                       agentData.custom_search_config_name || '';
        
        setEditFormData({
          name: agentData.name || '',
          instructions: agentData.instructions || '',
          description: agentData.description || '',
          model: agentData.model || '',
          temperature: agentData.temperature || 0,
          // Grounding settings from grounding_config (metadata)
          grounding_type: agentData.grounding_type || 'none',
          bing_count: count,
          bing_freshness: freshness,
          bing_market: market,
          bing_set_lang: setLang,
          // Azure Custom Search
          custom_search_config_name: customSearchConfigName,
          custom_count: count,
          custom_freshness: freshness,
          custom_market: market,
          custom_set_lang: setLang,
        });
        setShowEditPanel(true);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to load agent details');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load agent details');
    } finally {
      setLoadingAgent(false);
    }
  };

  // Update agent
  const handleUpdate = async () => {
    if (!editingAgent) return;

    try {
      setSaving(true);
      setError(null);

      const payload: any = {};
      
      // Only include fields that changed
      if (editFormData.name !== editingAgent.name) {
        payload.name = editFormData.name;
      }
      if (editFormData.instructions !== editingAgent.instructions) {
        payload.instructions = editFormData.instructions;
      }
      if (editFormData.description !== (editingAgent.description || '')) {
        payload.description = editFormData.description;
      }
      if (editFormData.model !== (editingAgent.model || '')) {
        payload.model = editFormData.model;
      }
      if (editFormData.temperature !== (editingAgent.temperature || 0)) {
        payload.temperature = editFormData.temperature;
      }

      // Update basic agent properties if changed
      let basicUpdateSuccess = true;
      if (Object.keys(payload).length > 0) {
        const response = await authenticatedFetch(`/api/admin/agents/${editingAgent.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || 'Failed to update agent');
          basicUpdateSuccess = false;
        }
      }

      // Update grounding if modified
      if (basicUpdateSuccess && groundingModified) {
        const groundingPayload: any = {
          grounding_type: editFormData.grounding_type,
        };

        if (editFormData.grounding_type === 'bing_search') {
          groundingPayload.bing_config = {
            count: editFormData.bing_count,
            freshness: editFormData.bing_freshness || undefined,
            market: editFormData.bing_market,
            set_lang: editFormData.bing_set_lang || undefined,
          };
        } else if (editFormData.grounding_type === 'bing_custom_search') {
          // Use configuration_name for ARM-managed configs
          groundingPayload.custom_search_config = {
            configuration_name: editFormData.custom_search_config_name,
            count: editFormData.custom_count,
            freshness: editFormData.custom_freshness || undefined,
            market: editFormData.custom_market,
            set_lang: editFormData.custom_set_lang || undefined,
          };
        }

        const groundingResponse = await authenticatedFetch(`/api/admin/agents/${editingAgent.id}/grounding`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(groundingPayload),
        });

        if (!groundingResponse.ok) {
          const errorData = await groundingResponse.json();
          setError(errorData.error || 'Failed to update grounding settings');
          setSaving(false);
          return;
        }

        // Get verification data from response
        const groundingData = await groundingResponse.json();
        if (groundingData.verification) {
          console.log('[AgentManagement] ✅ Grounding update VERIFIED:', groundingData.verification);
        }
      }

      // Check if anything was updated
      if (Object.keys(payload).length === 0 && !groundingModified) {
        setError('No changes detected');
        setSaving(false);
        return;
      }

      const verifiedMsg = groundingModified 
        ? ` (Grounding settings verified: count=${editFormData.grounding_type === 'bing_custom_search' ? editFormData.custom_count : editFormData.bing_count}, market=${editFormData.grounding_type === 'bing_custom_search' ? editFormData.custom_market : editFormData.bing_market})`
        : '';
      setSuccess(`Agent "${editFormData.name}" updated successfully${verifiedMsg}`);
      setShowEditPanel(false);
      setEditingAgent(null);
      setGroundingModified(false);
      loadAgents();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.message || 'Failed to update agent');
    } finally {
      setSaving(false);
    }
  };

  // Table columns
  const columns: IColumn[] = [
    {
      key: 'name',
      name: 'Agent Name',
      fieldName: 'name',
      minWidth: 150,
      maxWidth: 250,
      isResizable: true,
      onRender: (item: Agent) => (
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
          <BotRegular style={{ fontSize: 20, color: '#667eea' }} />
          <Text style={{ fontWeight: FontWeights.semibold }}>{item.name}</Text>
        </Stack>
      ),
    },
    {
      key: 'model',
      name: 'Model',
      fieldName: 'model',
      minWidth: 100,
      maxWidth: 150,
      isResizable: true,
      onRender: (item: Agent) => (
        <Text>{item.model || 'Default'}</Text>
      ),
    },
    {
      key: 'tools',
      name: 'Grounding',
      minWidth: 150,
      maxWidth: 200,
      onRender: (item: Agent) => {
        const groundingType = item.grounding_type || 'none';
        
        const getGroundingDisplay = () => {
          switch (groundingType) {
            case 'bing_search':
              return { icon: '🌐', label: 'Bing Web Search', color: '#667eea' };
            case 'bing_custom_search':
              return { icon: '🔍', label: 'Custom Search', color: '#107c10' };
            case 'file_search':
              return { icon: '📁', label: 'File Search', color: '#8764b8' };
            case 'code_interpreter':
              return { icon: '💻', label: 'Code Interpreter', color: '#e3008c' };
            case 'azure_ai_search':
              return { icon: '🔎', label: 'Azure AI Search', color: '#5c2d91' };
            default:
              return { icon: '', label: 'None', color: '#605e5c' };
          }
        };
        
        const display = getGroundingDisplay();
        
        return (
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
            <span>{display.icon}</span>
            <Text style={{ color: display.color, fontWeight: FontWeights.semibold }}>{display.label}</Text>
          </Stack>
        );
      },
    },
    {
      key: 'configName',
      name: 'Configuration',
      minWidth: 120,
      maxWidth: 180,
      onRender: (item: Agent) => {
        const configName = item.custom_search_config_name;
        
        if (!configName || item.grounding_type !== 'bing_custom_search') {
          return <Text style={{ color: '#a19f9d' }}>—</Text>;
        }
        
        return (
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
            <SettingsRegular style={{ color: '#107c10', fontSize: 14 }} />
            <Text style={{ color: '#107c10', fontWeight: FontWeights.semibold }}>{configName}</Text>
          </Stack>
        );
      },
    },
    {
      key: 'description',
      name: 'Description',
      fieldName: 'description',
      minWidth: 200,
      isResizable: true,
      onRender: (item: Agent) => (
        <Text style={{ color: '#605e5c' }}>{item.description || '-'}</Text>
      ),
    },
    {
      key: 'actions',
      name: 'Actions',
      minWidth: 100,
      maxWidth: 120,
      onRender: (item: Agent) => (
        <Stack horizontal tokens={{ childrenGap: 4 }}>
          <IconButton
            iconProps={{ iconName: 'Edit' }}
            title="Edit Agent"
            onClick={() => loadAgentDetails(item)}
            disabled={loadingAgent}
            styles={{ root: { color: '#667eea' } }}
          />
          <IconButton
            iconProps={{ iconName: 'Delete' }}
            title="Delete Agent"
            onClick={() => {
              setDeletingAgent(item);
              setShowDeleteDialog(true);
            }}
            styles={{ root: { color: '#d13438' } }}
          />
        </Stack>
      ),
    },
  ];

  // Render grounding-specific form fields
  const renderGroundingFields = () => {
    if (formData.grounding_type === 'bing_search') {
      return (
        <div className={styles.formSection}>
          <Text variant="mediumPlus" style={{ fontWeight: FontWeights.semibold, marginBottom: '12px', display: 'block' }}>
            <GlobeRegular style={{ marginRight: 8 }} />
            Bing Web Search Configuration
          </Text>
          <Stack tokens={{ childrenGap: 12 }}>
            <SpinButton
              label="Number of Results"
              labelPosition={0}
              min={1}
              max={50}
              step={1}
              value={String(formData.bing_count)}
              onChange={(_, value) => setFormData({ ...formData, bing_count: parseInt(value || '5') })}
            />
            <Dropdown
              label="Freshness"
              selectedKey={formData.bing_freshness}
              options={FRESHNESS_OPTIONS}
              onChange={(_, option) => setFormData({ ...formData, bing_freshness: option?.key as string || '' })}
            />
            <Dropdown
              label="Market/Locale"
              selectedKey={formData.bing_market}
              options={MARKET_OPTIONS}
              onChange={(_, option) => setFormData({ ...formData, bing_market: option?.key as string || 'he-IL' })}
            />
          </Stack>
        </div>
      );
    }

    if (formData.grounding_type === 'bing_custom_search') {
      // Build dropdown options from saved configs
      const configOptions: IDropdownOption[] = [
        { key: '', text: '-- Select a configuration --' },
        ...customSearchConfigs.map(c => ({
          key: c.name,  // Use name as key since that's what ARM API uses
          text: `${c.name} (${c.allowedDomains?.length || 0} allowed, ${c.blockedDomains?.length || 0} blocked)`
        }))
      ];

      const hasNoConfigs = customSearchConfigs.length === 0;

      return (
        <div className={styles.formSection}>
          <Text variant="mediumPlus" style={{ fontWeight: FontWeights.semibold, marginBottom: '12px', display: 'block' }}>
            <SearchRegular style={{ marginRight: 8 }} />
            Azure Bing Custom Search Configuration
          </Text>
          <Stack tokens={{ childrenGap: 12 }}>
            {hasNoConfigs ? (
              <MessageBar messageBarType={MessageBarType.warning}>
                <Stack tokens={{ childrenGap: 8 }}>
                  <Text variant="small" style={{ fontWeight: FontWeights.semibold }}>
                    ⚠️ No custom search configurations found
                  </Text>
                  <Text variant="small">
                    You need to create a configuration before using Bing Custom Search.
                    Click below to create one, then return here to assign it to this agent.
                  </Text>
                  <DefaultButton
                    text="Create Configuration"
                    iconProps={{ iconName: 'Add' }}
                    onClick={() => {
                      setShowCreatePanel(false);
                      setShowEditPanel(false);
                      setActiveTab('configs');
                    }}
                    styles={{ root: { marginTop: 8 } }}
                  />
                </Stack>
              </MessageBar>
            ) : (
              <MessageBar messageBarType={MessageBarType.info}>
                <Stack tokens={{ childrenGap: 4 }}>
                  <Text variant="small" style={{ fontWeight: FontWeights.semibold }}>
                    💡 Select a configuration to define allowed/blocked domains
                  </Text>
                  <Text variant="small">
                    Configurations define which domains the search will include or exclude.
                  </Text>
                </Stack>
              </MessageBar>
            )}

            <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
              <Stack.Item grow>
                <Dropdown
                  label="Search Configuration (Required)"
                  selectedKey={formData.custom_search_config_id}
                  options={configOptions}
                  onChange={(_, option) => {
                    const configName = option?.key as string || '';
                    const config = customSearchConfigs.find(c => c.name === configName) || null;
                    setSelectedCustomSearchConfig(config);
                    setFormData({ 
                      ...formData, 
                      custom_search_config_id: configName,
                    });
                  }}
                  placeholder="Select a saved configuration..."
                  required
                  disabled={hasNoConfigs}
                />
              </Stack.Item>
              <IconButton
                iconProps={{ iconName: 'Refresh' }}
                title="Refresh configurations"
                onClick={loadCustomSearchConfigs}
                styles={{ root: { marginBottom: 4 } }}
              />
            </Stack>

            {selectedCustomSearchConfig && (
              <MessageBar messageBarType={MessageBarType.success}>
                <Stack tokens={{ childrenGap: 4 }}>
                  <Text variant="small" style={{ fontWeight: FontWeights.semibold }}>
                    ✓ Using configuration: {selectedCustomSearchConfig.name}
                  </Text>
                  {selectedCustomSearchConfig.allowedDomains && selectedCustomSearchConfig.allowedDomains.length > 0 && (
                    <Text variant="small">
                      Allowed domains: {selectedCustomSearchConfig.allowedDomains.map(d => d.domain).join(', ')}
                    </Text>
                  )}
                  {selectedCustomSearchConfig.blockedDomains && selectedCustomSearchConfig.blockedDomains.length > 0 && (
                    <Text variant="small">
                      Blocked domains: {selectedCustomSearchConfig.blockedDomains.map(d => d.domain).join(', ')}
                    </Text>
                  )}
                </Stack>
              </MessageBar>
            )}

            <Separator />

            <SpinButton
              label="Number of Results"
              labelPosition={0}
              min={1}
              max={50}
              step={1}
              value={String(formData.custom_count)}
              onChange={(_, value) => setFormData({ ...formData, custom_count: parseInt(value || '5') })}
            />
            <Dropdown
              label="Freshness"
              selectedKey={formData.custom_freshness}
              options={FRESHNESS_OPTIONS}
              onChange={(_, option) => setFormData({ ...formData, custom_freshness: option?.key as string || '' })}
            />
            <Dropdown
              label="Market/Locale"
              selectedKey={formData.custom_market}
              options={MARKET_OPTIONS}
              onChange={(_, option) => setFormData({ ...formData, custom_market: option?.key as string || 'he-IL' })}
            />
            
            {!selectedCustomSearchConfig && (
              <MessageBar messageBarType={MessageBarType.warning}>
                Please select a Custom Search Configuration above. The configuration defines which domains are allowed/blocked for search.
              </MessageBar>
            )}
          </Stack>
        </div>
      );
    }

    return null;
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center" className={styles.header}>
        <Stack>
          <Text variant="xLarge" style={{ fontWeight: FontWeights.semibold }}>
            <BotRegular style={{ marginRight: 8, verticalAlign: 'middle' }} />
            AI Agent Management
          </Text>
          <Text variant="medium" style={{ color: '#605e5c' }}>
            Create and manage Azure AI Foundry agents with Bing grounding
          </Text>
        </Stack>
      </Stack>

      {/* Messages */}
      {error && (
        <MessageBar
          messageBarType={MessageBarType.error}
          onDismiss={() => setError(null)}
          dismissButtonAriaLabel="Close"
          styles={{ root: { marginBottom: 16 } }}
        >
          {error}
        </MessageBar>
      )}
      {success && (
        <MessageBar
          messageBarType={MessageBarType.success}
          onDismiss={() => setSuccess(null)}
          dismissButtonAriaLabel="Close"
          styles={{ root: { marginBottom: 16 } }}
        >
          {success}
        </MessageBar>
      )}

      {/* Tabs */}
      <Pivot
        selectedKey={activeTab}
        onLinkClick={(item) => setActiveTab(item?.props.itemKey || 'agents')}
        styles={{ root: { marginBottom: 16 } }}
      >
        <PivotItem headerText="Agents" itemKey="agents" itemIcon="Robot">
          {/* Agents Tab Content */}
          <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 8 }} styles={{ root: { marginBottom: 16 } }}>
            <TooltipHost content="Sync Custom Search system message to all agents using Bing Custom Search grounding">
              <DefaultButton
                iconProps={{ iconName: 'Sync' }}
                text={syncing ? 'Syncing...' : 'Sync Custom Search Instructions'}
                onClick={() => handleSyncCustomSearchInstructions(false)}
                disabled={!serviceStatus?.available || syncing}
                styles={{ root: { borderColor: '#667eea', color: '#667eea' } }}
              />
            </TooltipHost>
            <DefaultButton
              iconProps={{ iconName: 'Refresh' }}
              text="Refresh"
              onClick={() => { loadAgents(); loadServiceStatus(); }}
            />
            <PrimaryButton
              iconProps={{ iconName: 'Add' }}
              text="Create Agent"
              onClick={() => { resetForm(); setShowCreatePanel(true); }}
              disabled={!serviceStatus?.available}
            />
          </Stack>

          {/* Service Status */}
          <div className={styles.statusCard}>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
              {serviceStatus?.available ? (
                <CheckmarkCircleRegular style={{ fontSize: 24, color: '#107c10' }} />
              ) : (
                <WarningRegular style={{ fontSize: 24, color: '#d13438' }} />
              )}
              <Stack>
                <Text variant="mediumPlus" style={{ fontWeight: FontWeights.semibold }}>
                  Service Status: {serviceStatus?.available ? 'Available' : 'Unavailable'}
                </Text>
                <Text variant="small" style={{ color: '#605e5c' }}>
                  {serviceStatus?.message || 'Checking...'}
                </Text>
              </Stack>
            </Stack>
          </div>

          {/* Agents List */}
          {loading ? (
            <Stack horizontalAlign="center" verticalAlign="center" styles={{ root: { minHeight: 200 } }}>
              <Spinner size={SpinnerSize.large} label="Loading agents..." />
            </Stack>
          ) : agents.length === 0 ? (
            <Stack horizontalAlign="center" verticalAlign="center" styles={{ root: { minHeight: 200 } }}>
              <BotRegular style={{ fontSize: 48, color: '#c8c6c4', marginBottom: 16 }} />
              <Text variant="large" style={{ color: '#605e5c' }}>No agents found</Text>
              <Text variant="medium" style={{ color: '#a19f9d', marginBottom: 16 }}>
                Create your first agent to get started
              </Text>
              <PrimaryButton
                iconProps={{ iconName: 'Add' }}
                text="Create Agent"
                onClick={() => { resetForm(); setShowCreatePanel(true); }}
                disabled={!serviceStatus?.available}
              />
            </Stack>
          ) : (
            <DetailsList
              items={agents}
              columns={columns}
              selectionMode={SelectionMode.none}
              layoutMode={DetailsListLayoutMode.justified}
              isHeaderVisible={true}
            />
          )}
        </PivotItem>

        <PivotItem headerText="Custom Search Configs" itemKey="configs" itemIcon="Search">
          {/* Custom Search Configuration Management */}
          <CustomSearchConfigManagement onConfigChange={loadCustomSearchConfigs} />
        </PivotItem>
      </Pivot>

      {/* Create Agent Panel */}
      <Panel
        isOpen={showCreatePanel}
        onDismiss={() => { setShowCreatePanel(false); resetForm(); }}
        headerText="Create New Agent"
        type={PanelType.medium}
        closeButtonAriaLabel="Close"
      >
        <Stack tokens={{ childrenGap: 16 }} styles={{ root: { padding: '20px 0' } }}>
          <TextField
            label="Agent Name"
            required
            value={formData.name}
            onChange={(_, value) => setFormData({ ...formData, name: value || '' })}
            placeholder="my-search-agent"
            description="Unique name for the agent (alphanumeric and hyphens)"
          />

          <TextField
            label="Instructions (System Message)"
            required
            multiline
            rows={6}
            value={formData.instructions}
            onChange={(_, value) => setFormData({ ...formData, instructions: value || '' })}
            placeholder="You are a helpful assistant that searches the web for information..."
            description={adminDefaults.instructions 
              ? "Pre-filled from admin config. You can customize or keep as-is." 
              : "System instructions that define the agent's behavior"}
          />

          <TextField
            label="Description"
            value={formData.description}
            onChange={(_, value) => setFormData({ ...formData, description: value || '' })}
            placeholder="Optional description for the agent"
          />

          <TextField
            label="Model"
            value={formData.model}
            onChange={(_, value) => setFormData({ ...formData, model: value || '' })}
            placeholder={adminDefaults.model || "gpt-4o"}
            description={adminDefaults.model 
              ? `Default: ${adminDefaults.model} (from Azure Agent settings in admin)` 
              : "Leave empty to use the default model from admin settings"}
          />

          <SpinButton
            label="Temperature"
            labelPosition={0}
            min={0}
            max={2}
            step={0.1}
            value={String(formData.temperature)}
            onChange={(_, value) => setFormData({ ...formData, temperature: parseFloat(value || '0') })}
          />

          <Separator />

          <Dropdown
            label="Grounding Type"
            selectedKey={formData.grounding_type}
            options={GROUNDING_TYPES}
            onChange={(_, option) => setFormData({ ...formData, grounding_type: option?.key as string || 'none' })}
          />

          {renderGroundingFields()}

          <Separator />

          <Stack horizontal tokens={{ childrenGap: 8 }} horizontalAlign="end">
            <DefaultButton text="Cancel" onClick={() => { setShowCreatePanel(false); resetForm(); }} />
            <PrimaryButton
              text={saving ? 'Creating...' : 'Create Agent'}
              onClick={handleCreate}
              disabled={saving || !formData.name || !formData.instructions}
            />
          </Stack>
        </Stack>
      </Panel>

      {/* Edit Agent Panel */}
      <Panel
        isOpen={showEditPanel}
        onDismiss={() => { setShowEditPanel(false); setEditingAgent(null); }}
        headerText={`Edit Agent: ${editingAgent?.name || ''}`}
        type={PanelType.medium}
        closeButtonAriaLabel="Close"
      >
        {loadingAgent ? (
          <Stack horizontalAlign="center" verticalAlign="center" styles={{ root: { minHeight: 200 } }}>
            <Spinner size={SpinnerSize.large} label="Loading agent details..." />
          </Stack>
        ) : (
          <Stack tokens={{ childrenGap: 16 }} styles={{ root: { padding: '20px 0' } }}>
            <TextField
              label="Agent Name"
              required
              value={editFormData.name}
              onChange={(_, value) => setEditFormData({ ...editFormData, name: value || '' })}
              placeholder="my-search-agent"
              description="Unique name for the agent (alphanumeric and hyphens)"
            />

            <TextField
              label="Instructions (System Message)"
              required
              multiline
              rows={8}
              value={editFormData.instructions}
              onChange={(_, value) => setEditFormData({ ...editFormData, instructions: value || '' })}
              placeholder="You are a helpful assistant..."
              description="System instructions that define the agent's behavior"
            />

            <TextField
              label="Description"
              value={editFormData.description}
              onChange={(_, value) => setEditFormData({ ...editFormData, description: value || '' })}
              placeholder="Optional description for the agent"
            />

            <TextField
              label="Model"
              value={editFormData.model}
              onChange={(_, value) => setEditFormData({ ...editFormData, model: value || '' })}
              placeholder="gpt-4o"
              description="Model deployment name"
            />

            <SpinButton
              label="Temperature"
              labelPosition={0}
              min={0}
              max={2}
              step={0.1}
              value={String(editFormData.temperature)}
              onChange={(_, value) => setEditFormData({ ...editFormData, temperature: parseFloat(value || '0') })}
            />

            {/* Knowledge Sources / Grounding Info */}
            <Separator />
            <Text variant="mediumPlus" style={{ fontWeight: FontWeights.semibold }}>
              <GlobeRegular style={{ marginRight: 8 }} />
              Knowledge Sources (Grounding)
              {groundingModified && <Text style={{ color: '#667eea', marginLeft: 8, fontSize: 12 }}>• Modified</Text>}
            </Text>
            
            {/* Grounding Type Display */}
            <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
              <Text style={{ fontWeight: FontWeights.semibold, minWidth: 120 }}>Current Type:</Text>
              <Text style={{ 
                color: editingAgent?.tools_configured ? '#667eea' : '#605e5c', 
                backgroundColor: editingAgent?.tools_configured ? '#f0f6ff' : '#f5f5f5', 
                padding: '4px 12px', 
                borderRadius: '4px' 
              }}>
                {editFormData.grounding_type === 'bing_search' && '🌐 Bing Web Search'}
                {editFormData.grounding_type === 'bing_custom_search' && '🔍 Bing Custom Search'}
                {editFormData.grounding_type === 'code_interpreter' && '💻 Code Interpreter'}
                {editFormData.grounding_type === 'file_search' && '📁 File Search'}
                {editFormData.grounding_type === 'azure_ai_search' && '🔎 Azure AI Search'}
                {(!editFormData.grounding_type || editFormData.grounding_type === 'none') && '❌ No Grounding'}
              </Text>
            </Stack>

            {/* Editable Grounding Settings */}
            {(editFormData.grounding_type === 'bing_search' || editFormData.grounding_type === 'bing_grounding') && (
              <div className={styles.formSection}>
                <Text variant="mediumPlus" style={{ fontWeight: FontWeights.semibold, marginBottom: '12px', display: 'block' }}>
                  🌐 Bing Web Search Settings
                </Text>
                <Stack tokens={{ childrenGap: 12 }}>
                  <SpinButton
                    label="Number of Results"
                    labelPosition={0}
                    min={1}
                    max={50}
                    step={1}
                    value={String(editFormData.bing_count)}
                    onChange={(_, value) => {
                      setEditFormData({ ...editFormData, bing_count: parseInt(value || '5') });
                      setGroundingModified(true);
                    }}
                  />
                  <Dropdown
                    label="Freshness Filter"
                    selectedKey={editFormData.bing_freshness}
                    options={FRESHNESS_OPTIONS}
                    onChange={(_, option) => {
                      setEditFormData({ ...editFormData, bing_freshness: option?.key as string || '' });
                      setGroundingModified(true);
                    }}
                  />
                  <Dropdown
                    label="Market / Country"
                    selectedKey={editFormData.bing_market}
                    options={MARKET_OPTIONS}
                    onChange={(_, option) => {
                      setEditFormData({ ...editFormData, bing_market: option?.key as string || 'he-IL' });
                      setGroundingModified(true);
                    }}
                  />
                  <TextField
                    label="UI Language (optional)"
                    value={editFormData.bing_set_lang}
                    onChange={(_, value) => {
                      setEditFormData({ ...editFormData, bing_set_lang: value || '' });
                      setGroundingModified(true);
                    }}
                    placeholder="en, he, etc."
                    description="Language code for search UI"
                  />
                </Stack>
              </div>
            )}

            {editFormData.grounding_type === 'bing_custom_search' && (
              <div className={styles.formSection}>
                <Text variant="mediumPlus" style={{ fontWeight: FontWeights.semibold, marginBottom: '12px', display: 'block' }}>
                  🔍 Azure Bing Custom Search Settings
                </Text>
                <Stack tokens={{ childrenGap: 12 }}>
                  {customSearchConfigs.length === 0 ? (
                    <MessageBar messageBarType={MessageBarType.warning}>
                      <Stack tokens={{ childrenGap: 8 }}>
                        <Text variant="small" style={{ fontWeight: FontWeights.semibold }}>
                          ⚠️ No custom search configurations found
                        </Text>
                        <Text variant="small">
                          Create a configuration in the "Custom Search Configs" tab first.
                        </Text>
                        <DefaultButton
                          text="Go to Configurations"
                          iconProps={{ iconName: 'Settings' }}
                          onClick={() => {
                            setShowEditPanel(false);
                            setActiveTab('configs');
                          }}
                          styles={{ root: { marginTop: 8 } }}
                        />
                      </Stack>
                    </MessageBar>
                  ) : (
                    <>
                      <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
                        <Stack.Item grow>
                          <Dropdown
                            label="Search Configuration (Required)"
                            selectedKey={editFormData.custom_search_config_name}
                            options={[
                              { key: '', text: '-- Select a configuration --' },
                              ...customSearchConfigs.map(c => ({
                                key: c.name,
                                text: `${c.name} (${c.allowedDomains?.length || 0} allowed, ${c.blockedDomains?.length || 0} blocked)`
                              }))
                            ]}
                            onChange={(_, option) => {
                              setEditFormData({ ...editFormData, custom_search_config_name: option?.key as string || '' });
                              setGroundingModified(true);
                            }}
                            required
                          />
                        </Stack.Item>
                        <IconButton
                          iconProps={{ iconName: 'Refresh' }}
                          title="Refresh configurations"
                          onClick={loadCustomSearchConfigs}
                          styles={{ root: { marginBottom: 4 } }}
                        />
                      </Stack>
                      
                      {!editFormData.custom_search_config_name && (
                        <MessageBar messageBarType={MessageBarType.warning}>
                          Please select a Custom Search Configuration to define which domains are allowed/blocked.
                        </MessageBar>
                      )}
                    </>
                  )}

                  <SpinButton
                    label="Number of Results"
                    labelPosition={0}
                    min={1}
                    max={50}
                    step={1}
                    value={String(editFormData.custom_count)}
                    onChange={(_, value) => {
                      setEditFormData({ ...editFormData, custom_count: parseInt(value || '5') });
                      setGroundingModified(true);
                    }}
                  />
                  <Dropdown
                    label="Freshness Filter"
                    selectedKey={editFormData.custom_freshness}
                    options={FRESHNESS_OPTIONS}
                    onChange={(_, option) => {
                      setEditFormData({ ...editFormData, custom_freshness: option?.key as string || '' });
                      setGroundingModified(true);
                    }}
                  />
                  <Dropdown
                    label="Market / Country"
                    selectedKey={editFormData.custom_market}
                    options={MARKET_OPTIONS}
                    onChange={(_, option) => {
                      setEditFormData({ ...editFormData, custom_market: option?.key as string || 'he-IL' });
                      setGroundingModified(true);
                    }}
                  />
                  <TextField
                    label="UI Language (optional)"
                    value={editFormData.custom_set_lang}
                    onChange={(_, value) => {
                      setEditFormData({ ...editFormData, custom_set_lang: value || '' });
                      setGroundingModified(true);
                    }}
                    placeholder="en, he, etc."
                  />
                </Stack>
              </div>
            )}

            {(editFormData.grounding_type === 'file_search' || editFormData.grounding_type === 'azure_ai_search' || editFormData.grounding_type === 'code_interpreter') && (
              <MessageBar messageBarType={MessageBarType.info}>
                <Text variant="small">
                  This grounding type ({editFormData.grounding_type.replace(/_/g, ' ')}) has limited editable settings. 
                  The configuration is managed through Azure AI Foundry.
                </Text>
              </MessageBar>
            )}

            {(!editFormData.grounding_type || editFormData.grounding_type === 'none') && (
              <MessageBar messageBarType={MessageBarType.warning}>
                <Text variant="small">
                  This agent has no grounding tools configured. It will not search external knowledge sources.
                  To add grounding, you can change the grounding type when creating a new agent.
                </Text>
              </MessageBar>
            )}

            <Separator />

            <Stack horizontal tokens={{ childrenGap: 8 }} horizontalAlign="end">
              <DefaultButton text="Cancel" onClick={() => { setShowEditPanel(false); setEditingAgent(null); }} />
              <PrimaryButton
                text={saving ? 'Saving...' : 'Save Changes'}
                onClick={handleUpdate}
                disabled={saving || !editFormData.name || !editFormData.instructions}
              />
            </Stack>
          </Stack>
        )}
      </Panel>

      {/* Delete Confirmation Dialog */}
      <Dialog
        hidden={!showDeleteDialog}
        onDismiss={() => { setShowDeleteDialog(false); setDeletingAgent(null); }}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Delete Agent',
          subText: `Are you sure you want to delete the agent "${deletingAgent?.name}"? This action cannot be undone.`,
        }}
      >
        <DialogFooter>
          <DefaultButton onClick={() => { setShowDeleteDialog(false); setDeletingAgent(null); }} text="Cancel" />
          <PrimaryButton onClick={handleDelete} text={saving ? 'Deleting...' : 'Delete'} disabled={saving} styles={{ root: { backgroundColor: '#d13438' } }} />
        </DialogFooter>
      </Dialog>
    </div>
  );
};

export default AgentManagement;
